import React, { useState, useEffect } from 'react';
import ApiService from '@/services/api';
import Swal from 'sweetalert2';
import { formatRupiah } from '@/helpers/utils';
import dayjs from 'dayjs';

const TransactionDetailModal = ({ isOpen, onClose, transaction, onApprove, onCancel }) => {
  const [loading, setLoading] = useState(false);
  const [transactionDetail, setTransactionDetail] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [selectedDebitAccount, setSelectedDebitAccount] = useState('');
  const [selectedCreditAccount, setSelectedCreditAccount] = useState('');
  const [loadingAccounts, setLoadingAccounts] = useState(false);

  useEffect(() => {
    if (isOpen && transaction) {
      fetchTransactionDetail();
      fetchAccounts();
    }
  }, [isOpen, transaction]);

  const fetchTransactionDetail = async () => {
    try {
      setLoading(true);
      const response = await ApiService.get(`/savings/transactions/${transaction.id}/detail`);
      setTransactionDetail(response.data);
    } catch (error) {
      console.error('Error fetching transaction detail:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error!',
        text: 'Gagal memuat detail transaksi'
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchAccounts = async () => {
    try {
      setLoadingAccounts(true);
      // Get user from localStorage to get cooperative_id
      const userStr = localStorage.getItem('user_profile');
      const user = userStr ? JSON.parse(userStr) : null;
      const cooperativeId = user?.cooperative?.id || 0;

      // Fetch ASSET accounts for debit (Cash/Bank)
      const assetResponse = await ApiService.get(
        `/api/accounting/chart-of-accounts/by-type?cooperative_id=${cooperativeId}&account_type=ASSET&account_group=CURRENT_ASSET`
      );

      // Fetch EQUITY accounts for credit (Savings)
      const equityResponse = await ApiService.get(
        `/api/accounting/chart-of-accounts/by-type?cooperative_id=${cooperativeId}&account_type=EQUITY&account_group=MEMBER_EQUITY`
      );

      setAccounts({
        assets: assetResponse.data || [],
        equities: equityResponse.data || []
      });
    } catch (error) {
      console.error('Error fetching accounts:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error!',
        text: 'Gagal memuat data akun. Pastikan Chart of Accounts sudah dibuat.'
      });
    } finally {
      setLoadingAccounts(false);
    }
  };

  const handleApprove = async () => {
    if (!selectedDebitAccount || !selectedCreditAccount) {
      Swal.fire({
        icon: 'warning',
        title: 'Perhatian!',
        text: 'Silakan pilih akun Debit dan Credit terlebih dahulu'
      });
      return;
    }

    const result = await Swal.fire({
      title: 'Konfirmasi Approve',
      text: 'Apakah Anda yakin ingin menyetujui transaksi ini?',
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Ya, Approve!',
      cancelButtonText: 'Batal'
    });

    if (result.isConfirmed) {
      await onApprove(transaction.id, selectedDebitAccount, selectedCreditAccount);
      onClose();
    }
  };

  const handleCancel = async () => {
    const result = await Swal.fire({
      title: 'Konfirmasi Cancel',
      text: 'Apakah Anda yakin ingin membatalkan transaksi ini?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Ya, Batalkan!',
      cancelButtonText: 'Tidak'
    });

    if (result.isConfirmed) {
      await onCancel(transaction.id);
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center">
          <h2 className="text-2xl font-bold text-gray-900">Detail Transaksi & Mapping COA</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="px-6 py-4">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
              <span className="ml-3 text-gray-600">Memuat detail transaksi...</span>
            </div>
          ) : transactionDetail ? (
            <div className="space-y-6">
              {/* Transaction Info */}
              <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                <h3 className="text-lg font-semibold text-blue-900 mb-3">Informasi Transaksi</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-gray-600">Nomor Transaksi</label>
                    <p className="text-gray-900 font-semibold">{transactionDetail.transaction_number}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-600">Tanggal Transaksi</label>
                    <p className="text-gray-900">{dayjs(transactionDetail.transaction_date).format('DD/MM/YYYY HH:mm')}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-600">Tipe Transaksi</label>
                    <p className="text-gray-900 capitalize">{transactionDetail.transaction_type}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-600">Jumlah</label>
                    <p className="text-gray-900 font-bold text-lg">{formatRupiah(transactionDetail.amount)}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-600">Status</label>
                    <span className={`inline-block px-3 py-1 rounded-full text-sm font-semibold ${
                      transactionDetail.status === 'completed' ? 'bg-green-100 text-green-800' :
                      transactionDetail.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-red-100 text-red-800'
                    }`}>
                      {transactionDetail.status}
                    </span>
                  </div>
                </div>
              </div>

              {/* Member & Account Info */}
              <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Informasi Anggota & Rekening</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-gray-600">Nama Anggota</label>
                    <p className="text-gray-900">{transactionDetail.member?.full_name}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-600">No. Anggota</label>
                    <p className="text-gray-900">{transactionDetail.member?.member_id_number}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-600">No. Rekening</label>
                    <p className="text-gray-900">{transactionDetail.savings_account?.account_number}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-600">Jenis Simpanan</label>
                    <p className="text-gray-900">{transactionDetail.savings_account?.savings_type_name}</p>
                  </div>
                </div>
              </div>

              {/* Payment Details */}
              {transactionDetail.payment_details && (
                <div className="bg-green-50 rounded-lg p-4 border border-green-200">
                  <h3 className="text-lg font-semibold text-green-900 mb-3">Detail Pembayaran</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium text-gray-600">Bank</label>
                      <p className="text-gray-900">{transactionDetail.payment_details.bank_name}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-600">No. Rekening</label>
                      <p className="text-gray-900">{transactionDetail.payment_details.bank_account_number}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-600">Atas Nama</label>
                      <p className="text-gray-900">{transactionDetail.payment_details.bank_account_name}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* COA Mapping Section - Only show for pending transactions */}
              {transactionDetail.status === 'pending' || transactionDetail.status === 'waiting_for_confirmation' ? (
                <div className="bg-yellow-50 rounded-lg p-4 border border-yellow-200">
                  <h3 className="text-lg font-semibold text-yellow-900 mb-3">
                    <i className="mgc_safe_line mr-2"></i>
                    Mapping Chart of Accounts (Jurnal)
                  </h3>
                  <p className="text-sm text-gray-600 mb-4">
                    Pilih akun COA untuk mencatat transaksi ini ke jurnal akuntansi.
                    {transactionDetail.transaction_type === 'deposit' && (
                      <span className="block mt-1">
                        <strong>Untuk Deposit:</strong> Debit = Kas/Bank (Asset), Credit = Simpanan Anggota (Equity)
                      </span>
                    )}
                    {transactionDetail.transaction_type === 'withdrawal' && (
                      <span className="block mt-1">
                        <strong>Untuk Penarikan:</strong> Debit = Simpanan Anggota (Equity), Credit = Kas/Bank (Asset)
                      </span>
                    )}
                  </p>

                  {loadingAccounts ? (
                    <div className="text-center py-4">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-yellow-600 mx-auto"></div>
                      <p className="text-sm text-gray-600 mt-2">Memuat data akun...</p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {/* Debit Account */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Akun Debit {transactionDetail.transaction_type === 'deposit' ? '(Kas/Bank - Asset)' : '(Simpanan - Equity)'}
                          <span className="text-red-500">*</span>
                        </label>
                        <select
                          value={selectedDebitAccount}
                          onChange={(e) => setSelectedDebitAccount(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          required
                        >
                          <option value="">-- Pilih Akun Debit --</option>
                          {transactionDetail.transaction_type === 'deposit' 
                            ? accounts.assets?.map(acc => (
                                <option key={acc.id} value={acc.id}>
                                  {acc.account_code} - {acc.account_name}
                                </option>
                              ))
                            : accounts.equities?.map(acc => (
                                <option key={acc.id} value={acc.id}>
                                  {acc.account_code} - {acc.account_name}
                                </option>
                              ))
                          }
                        </select>
                      </div>

                      {/* Credit Account */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Akun Credit {transactionDetail.transaction_type === 'deposit' ? '(Simpanan - Equity)' : '(Kas/Bank - Asset)'}
                          <span className="text-red-500">*</span>
                        </label>
                        <select
                          value={selectedCreditAccount}
                          onChange={(e) => setSelectedCreditAccount(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          required
                        >
                          <option value="">-- Pilih Akun Credit --</option>
                          {transactionDetail.transaction_type === 'deposit'
                            ? accounts.equities?.map(acc => (
                                <option key={acc.id} value={acc.id}>
                                  {acc.account_code} - {acc.account_name}
                                </option>
                              ))
                            : accounts.assets?.map(acc => (
                                <option key={acc.id} value={acc.id}>
                                  {acc.account_code} - {acc.account_name}
                                </option>
                              ))
                          }
                        </select>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="bg-gray-100 rounded-lg p-4 border border-gray-300 text-center">
                  <p className="text-gray-600">
                    Transaksi ini sudah diproses. Mapping COA tidak diperlukan.
                  </p>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-12 text-gray-500">
              Tidak ada data transaksi
            </div>
          )}
        </div>

        {/* Footer Actions */}
        {transactionDetail && (transactionDetail.status === 'pending' || transactionDetail.status === 'waiting_for_confirmation') && (
          <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 px-6 py-4 flex justify-end gap-3">
            <button
              onClick={handleCancel}
              className="px-6 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors font-semibold"
            >
              <i className="mgc_close_line mr-2"></i>
              Tolak / Cancel
            </button>
            <button
              onClick={handleApprove}
              disabled={!selectedDebitAccount || !selectedCreditAccount || loadingAccounts}
              className="px-6 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors font-semibold disabled:bg-gray-300 disabled:cursor-not-allowed"
            >
              <i className="mgc_check_line mr-2"></i>
              Approve & Catat ke Jurnal
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default TransactionDetailModal;
